import { ContentItem } from "@api";
import VadImage from "vadimages-nextjs-image-optimizer";
import React from "react";
import FirstModularPodSwiper from "./Sliders/FirstModularPodSwiper";
import * as C from "@/components";
import { VimeoBackground } from "@/components/Video/VimeoBackground";
import { VideoBackground } from "@/components/Video/VideoBackground";

interface ISingularFirstModularPodSectionProps {
  pageContent: Record<string, ContentItem>;
}

type CardTitleType = "Ceramic" | "Stainless" | "Postless";

interface IFirstModularPodSectionCardProps {
  pageContent: Record<string, ContentItem>;
  cardIndex: number;
  cardTitle: CardTitleType;
  className?: string;
}

const Badge = ({
  cardTitle,
  cardIndex,
  className,
}: {
  cardTitle: string;
  cardIndex: number;
  className?: string;
}) => {
  const gradientDirections = [
    "bg-linear-to-t",
    "bg-linear-to-tr",
    "bg-linear-to-r",
    "bg-linear-to-br",
    "bg-linear-to-b",
    "bg-linear-to-bl",
    "bg-linear-to-l",
    "bg-linear-to-tl",
  ];

  const gradientPairs = ["", "", "", "", "", "", "", "", "", ""];

  return (
    <div
      className={`relative w-fit mx-auto rounded-[40px] py-2.5 px-6 lg:px-8 overflow-hidden ${className}`}
    >
      <p className="text-white text-[20px] lg:text-2xl font-semibold relative z-10">
        {cardTitle}
      </p>
      <VadImage
        src={`/images/singular/gradient.png`}
        alt="Background"
        width={1600}
        height={1600}
        className={`pointer-events-none absolute inset-0 w-full h-full object-cover z-0`}
      />
    </div>
  );
};

const firstModularPodSectionCardListItems = {
  Ceramic: ["Live rosin", "Live resin", "Premium extracts"],
  Postless: ["Live rosin", "Live resin", "Distillate"],
  Stainless: ["Distillate", "THCA, HHC, D8, D9", "Liquid diamonds"],
};

export const FirstModularPodSectionCard = ({
  cardTitle,
  cardIndex,
  pageContent,
  className,
}: IFirstModularPodSectionCardProps) => {
  const lowercasedTitle = cardTitle.toLowerCase().replace(" ", "-");
  return (
    <div
      className={`bg-muted-tan rounded-default px-6 py-6 md:py-16 h-full ${
        className ?? ""
      }`}
    >
      <Badge
        cardTitle={cardTitle}
        cardIndex={cardIndex}
        className="max-md:w-full text-center"
      />
      <VadImage
        src={`/images/singular/pod-${cardTitle.toLowerCase()}.1.png`}
        alt={`Card-${lowercasedTitle}`}
        width={230}
        height={290}
        className="mx-auto py-8"
      />
      <ul className="space-y-5 w-fit mx-auto">
        {firstModularPodSectionCardListItems[cardTitle].map(
          (cardListItem, liIndex) => (
            <li
              key={`${cardTitle}-list-item-${liIndex}`}
              className="flex gap-1.5 items-center"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 0C8.8174 0 5.76515 1.26428 3.51472 3.51472C1.26428 5.76515 0 8.8174 0 12C4.74244e-08 15.1826 1.26428 18.2348 3.51472 20.4853C5.76515 22.7357 8.8174 24 12 24C15.1826 24 18.2348 22.7357 20.4853 20.4853C22.7357 18.2348 24 15.1826 24 12C24 8.8174 22.7357 5.76515 20.4853 3.51472C18.2348 1.26428 15.1826 0 12 0ZM10.675 17.3025L5.9 12.53L8.02 10.41L10.675 13.06L15.9775 7.75L18.0975 9.87L10.675 17.3025Z"
                  fill="#7A756C"
                />
              </svg>
              <span>{cardListItem}</span>
            </li>
          )
        )}
      </ul>
    </div>
  );
};

const SingularFirstModularPodSection = ({
  pageContent,
}: ISingularFirstModularPodSectionProps) => {
  return (
    <>
      <C.Card className="w-full py-20 hidden md:block">
        <div className="text-center mb-6">
          <h4 className="text-3xl pb-3">The right pod for every extract</h4>
          <p className="text-lg">
            Whatever your extract, Singular has you covered.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6">
          {(["Ceramic", "Postless", "Stainless"] as CardTitleType[]).map(
            (cardTitle, cardIndex) => (
              <FirstModularPodSectionCard
                cardTitle={cardTitle}
                cardIndex={cardIndex}
                pageContent={pageContent}
                key={`modular-card-${cardTitle}`}
              />
            )
          )}
        </div>
      </C.Card>
      <div className="block md:hidden w-full">
        <div className="text-center mb-6">
          <h4 className="text-xl pb-3">The right pod for every extract</h4>
          <p>Whatever your extract, Singular has you covered.</p>
        </div>
        <FirstModularPodSwiper pageContent={pageContent}>
          {(["Ceramic", "Postless", "Stainless"] as CardTitleType[]).map(
            (cardTitle, cardIndex) => (
              <FirstModularPodSectionCard
                cardTitle={cardTitle}
                cardIndex={cardIndex}
                pageContent={pageContent}
                key={`modular-card-${cardTitle}`}
              />
            )
          )}
        </FirstModularPodSwiper>
      </div>

      <div className="relative bg-muted-tan rounded-default mt-6 pt-10 py-6 md:pt-10 md:pl-24 text-warm-gray-copy">
        <div className="block md:hidden text-center mb-5">
          <h4 className="font-semibold text-xl mx-auto mb-2.5">
            Dial in your volume
          </h4>
          <p className="max-sm:max-w-[231px] mx-auto">Pods from 0.5mL to 5mL for every market</p>
        </div>
        <div className="hidden md:block md:absolute md:left-16 md:top-16 lg:left-32 lg:top-32 text-left">
          <h4 className="font-semibold text-3xl leading-[38px] mx-auto">
            Dial in your volume
          </h4>
          <p>Pods from 0.5mL to 5mL for every market</p>
        </div>

        <div className="relative">
          <VadImage
            src={"/images/singular/pod-volumes.1.png"}
            alt={"Pod volumes"}
            width={1181}
            height={580}
          />
          <div className="hidden absolute top-0 left-0 w-full h-full md:pl-24 md:pr-32 lg:pl-36 lg:pr-44 md:pb-8 lg:pb-16 md:flex flex-col justify-end">
            <div className="w-full py-4 grid grid-cols-4 text-center text-[12px]">
              <p>0.5mL/1mL</p>
              <p>2mL</p>
              <p>3mL</p>
              <p>4mL/5mL</p>
            </div>
          </div>
        </div>
      </div>

      <C.Card className="relative mt-6 py-10 md:pb-16 md:pt-11 md:px-48 px-6">
        <div className="block md:hidden text-center mb-5">
          <h4 className="font-semibold text-xl mx-auto mb-2">
            Power that fits every pod
          </h4>
          <p className="max-w-[480px]">
            Singular batteries work with every Singular pod, giving you
            unmatched versatility across all your product lines.
          </p>
        </div>
        <div className="hidden md:block absolute md:left-16 md:top-16 lg:left-32 lg:top-24 text-left">
          <h4 className="font-semibold text-3xl leading-[38px] mx-auto mb-2">
            Power that fits every pod
          </h4>
          <p className="max-w-[480px]">
            Singular batteries work with every Singular pod, giving you
            unmatched versatility across all your product lines.
          </p>
        </div>

        <div className="grid grid-cols-2">
          {["Offset", "Axil"].map((card) => (
            <div
              key={`power-that-fits-every-pod-${card}`}
              className="flex flex-col justify-end"
            >
              <VadImage
                src={`/images/singular/battery-${card.toLowerCase()}.${card === "Offset" ? '1.' : ''}png`}
                alt={`Power that fits every pod: ${card}`}
                width={card === "Offset" ? 355 : 146}
                height={card === "Offset" ? 397 : 589}
                className="mx-auto mb-6 hidden md:block"
              />
              <VadImage
                src={`/images/singular/battery-${card.toLowerCase()}.${card === "Offset" ? '1.' : ''}png`}
                alt={`Power that fits every pod: ${card}`}
                width={card === "Offset" ? 133 : 72}
                height={card === "Offset" ? 203 : 290}
                className="mx-auto mb-6 block md:hidden"
              />
              <p className="text-center text-[12px]">{card} S1 Battery</p>
            </div>
          ))}
        </div>

        <div className="mt-28 mx-auto min-w-[336px] lg:min-w-[713px] min-h-[295px] lg:min-h-[642px] rounded-default overflow-hidden relative hidden md:block">
          <VideoBackground videoSrc="https://images.active710.com/video/singular/battery-animation-1080-crop.mp4" />
        </div>
      </C.Card>

      <C.Card className="relative block md:hidden mt-6 min-h-[295px]">
        <VideoBackground videoSrc="https://images.active710.com/video/singular/battery-animation-1080-crop.mp4" />
      </C.Card>
    </>
  );
};

export default SingularFirstModularPodSection;
